import { Suspense, lazy, useMemo } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClientProvider } from "@tanstack/react-query";

import { queryClient } from "./shared/api/queryClient";
import { ColorModeProvider, useColorMode } from "./context/ColorModeContext";
import { OrgIdProvider } from "./shared/auth/orgContext";
import { ToastProvider } from "./shared/toast";
import { buildKardaMuiTheme } from "./theme/buildKardaMuiTheme";
import { AuthTokenBridge } from "./components/auth/AuthTokenBridge";
import { ProtectedView } from "./components/layout/ProtectedView";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import { RouteErrorBoundary } from "./components/errors/RouteErrorBoundary";
import { RequireIncidentManagement } from "./components/auth/RequireIncidentManagement";
import { RequirePermission } from "./components/auth/RequirePermission";
import {
  EVIDENCE_EDIT_OWN_PERMISSION,
  EVIDENCE_EDIT_STATION_PERMISSION,
  EVIDENCE_READ_PERMISSION,
} from "./shared/auth/authorization";
import LoginPage from "./pages/Login";
import { InitiateLogin } from "./pages/InitiateLogin";
import { AuthCallback } from "./pages/AuthCallback";
import NotFound404 from "./pages/NotFound";
import { accountRoutes } from "./routes/accountRoutes";

// Keep evidence review and incident dependencies out of the login bundle.
const IncidentManagementPage = lazy(() => import("./pages/IncidentManagement"));
const IncidentDetailPage = lazy(
  () => import("./pages/IncidentManagement/IncidentDetailPage"),
);
const TranscriptViewPage = lazy(
  () => import("./pages/Transcripts/TranscriptViewPage"),
);
const CaseTimelinePage = lazy(
  () => import("./pages/IncidentManagement/CaseTimelinePage"),
);

function AppContent() {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildKardaMuiTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <Router>
          <AuthTokenBridge />
          <Routes>
            <Route
              path="/login"
              element={
                <RouteErrorBoundary>
                  <LoginPage />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/initiate-login"
              element={
                <RouteErrorBoundary>
                  <InitiateLogin />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/callback"
              element={
                <RouteErrorBoundary>
                  <AuthCallback />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedView>
                  <RouteErrorBoundary>
                    <Outlet />
                  </RouteErrorBoundary>
                </ProtectedView>
              }
            >
              {/* Preserve incident and evidence-editor deep links. */}
              <Route index element={<Navigate to="/incidents" replace />} />
              {accountRoutes}
              <Route
                path="incidents"
                element={
                  <RequireIncidentManagement>
                    <Suspense fallback={null}>
                      <IncidentManagementPage />
                    </Suspense>
                  </RequireIncidentManagement>
                }
              />
              <Route
                path="incidents/:incidentId"
                element={
                  <RequireIncidentManagement>
                    <Suspense fallback={null}>
                      <IncidentDetailPage />
                    </Suspense>
                  </RequireIncidentManagement>
                }
              />
              <Route
                path="transcripts/:evidenceId"
                element={
                  <RequireIncidentManagement>
                    <RequirePermission
                      permission={[
                        EVIDENCE_READ_PERMISSION,
                        EVIDENCE_EDIT_OWN_PERMISSION,
                        EVIDENCE_EDIT_STATION_PERMISSION,
                      ]}
                    >
                      <Suspense fallback={null}>
                        <TranscriptViewPage />
                      </Suspense>
                    </RequirePermission>
                  </RequireIncidentManagement>
                }
              />
              <Route
                path="incidents/:incidentId/timeline"
                element={
                  <RequireIncidentManagement>
                    <Suspense fallback={null}>
                      <CaseTimelinePage />
                    </Suspense>
                  </RequireIncidentManagement>
                }
              />
            </Route>
            <Route
              path="*"
              element={
                <RouteErrorBoundary>
                  <NotFound404 />
                </RouteErrorBoundary>
              }
            />
          </Routes>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary variant="bare">
      {/* Above ColorModeProvider/OrgIdProvider: the query layer has no theme or
          auth dependency, and the module-singleton client must sit over
          everything that will query. */}
      <QueryClientProvider client={queryClient}>
        <ColorModeProvider>
          <OrgIdProvider>
            <AppContent />
          </OrgIdProvider>
        </ColorModeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
